import { useReducer, useRef } from "react";
import { LocalDate } from "@js-joda/core";
import {
  DatePickerMenuPosition,
  DatePickerRange,
  SelectionMode,
} from "./datePicker.types";
import { normalizeToFirstOfMonth } from "./datePicker.utils";

export interface DatePickerReducerState {
  mode: SelectionMode;
  isOpen: boolean;
  pendingStart: LocalDate | null;
  range: DatePickerRange;
  isFocused: boolean;
  draftValue: string;
  multiSelectedKeys: Set<string>;
  committedRange: DatePickerRange;
  committedMultiKeys: Set<string>;
  menuPosition: DatePickerMenuPosition | null;
  currentMonth: LocalDate;
  localExcludeHolidays: boolean;
  localExcludeWeekends: boolean;
}

export type DatePickerAction =
  | { type: "set-mode"; mode: SelectionMode }
  | { type: "set-open"; isOpen: boolean }
  | { type: "set-focused"; isFocused: boolean }
  | { type: "set-draft-value"; draftValue: string }
  | { type: "set-pending-start"; pendingStart: LocalDate | null }
  | { type: "set-range"; range: DatePickerRange }
  | { type: "set-multi-selected"; multiSelectedKeys: Set<string> }
  | { type: "set-committed-range"; committedRange: DatePickerRange }
  | { type: "set-committed-multi"; committedMultiKeys: Set<string> }
  | { type: "set-menu-position"; menuPosition: DatePickerMenuPosition | null }
  | { type: "set-current-month"; currentMonth: LocalDate }
  | { type: "sync-from-committed" }
  | { type: "reset-selection" }
  | { type: "set-local-exclude-holidays"; value: boolean }
  | { type: "set-local-exclude-weekends"; value: boolean };

export interface UseDatePickerStateResult {
  state: DatePickerReducerState;
  dispatch: React.Dispatch<DatePickerAction>;
  containerRef: React.RefObject<HTMLDivElement>;
  triggerRef: React.RefObject<HTMLInputElement>;
  popupRef: React.RefObject<HTMLDivElement>;
}

const reducer = (
  state: DatePickerReducerState,
  action: DatePickerAction,
): DatePickerReducerState => {
  switch (action.type) {
    case "set-mode":
      return { ...state, mode: action.mode };
    case "set-open":
      return { ...state, isOpen: action.isOpen };
    case "set-focused":
      return { ...state, isFocused: action.isFocused };
    case "set-draft-value":
      return { ...state, draftValue: action.draftValue };
    case "set-pending-start":
      return { ...state, pendingStart: action.pendingStart };
    case "set-range":
      return { ...state, range: action.range };
    case "set-multi-selected":
      return { ...state, multiSelectedKeys: action.multiSelectedKeys };
    case "set-committed-range":
      return { ...state, committedRange: action.committedRange };
    case "set-committed-multi":
      return { ...state, committedMultiKeys: action.committedMultiKeys };
    case "set-menu-position":
      return { ...state, menuPosition: action.menuPosition };
    case "set-current-month":
      return { ...state, currentMonth: action.currentMonth };
    case "sync-from-committed":
      return {
        ...state,
        range: { ...state.committedRange },
        multiSelectedKeys: new Set(state.committedMultiKeys),
        pendingStart: null,
      };
    case "reset-selection":
      return {
        ...state,
        pendingStart: null,
        range: { start: null, end: null },
        multiSelectedKeys: new Set(),
        committedRange: { start: null, end: null },
        committedMultiKeys: new Set(),
      };
    case "set-local-exclude-holidays":
      return { ...state, localExcludeHolidays: action.value };
    case "set-local-exclude-weekends":
      return { ...state, localExcludeWeekends: action.value };
    default:
      return state;
  }
};

export const useDatePickerState = (
  initialMonth: LocalDate,
  initialMode: SelectionMode,
): UseDatePickerStateResult => {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLInputElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const [state, dispatch] = useReducer(
    reducer,
    { initialMonth, initialMode },
    (arg): DatePickerReducerState => ({
      mode: arg.initialMode,
      isOpen: false,
      pendingStart: null,
      range: { start: null, end: null },
      isFocused: false,
      draftValue: "",
      multiSelectedKeys: new Set(),
      committedRange: { start: null, end: null },
      committedMultiKeys: new Set(),
      menuPosition: null,
      currentMonth: normalizeToFirstOfMonth(arg.initialMonth),
      localExcludeHolidays: false,
      localExcludeWeekends: false,
    }),
  );

  return {
    state,
    dispatch,
    containerRef,
    triggerRef,
    popupRef,
  };
};
